import React, { useEffect, useState } from 'react';
import {
    GoogleAuthProvider,
    isSignInWithEmailLink,
    sendSignInLinkToEmail,
    signInWithEmailLink,
    signInWithPopup,
} from 'firebase/auth';
import AuthInput from '../components/auth/AuthInput';
import AuthScreen from '../components/auth/AuthScreen';
import PrimaryButton from '../components/auth/PrimaryButton';
import SecondaryButton from '../components/auth/SecondaryButton';
import { auth } from '../firebase/config';
import { runAsyncUi } from '../utils/asyncUi';

const EMAIL_KEY = 'pmapper_email_for_signin';

const Login: React.FC = () => {
    const [email, setEmail] = useState('');
    const [sent, setSent] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!isSignInWithEmailLink(auth, window.location.href)) return;
        const saved = localStorage.getItem(EMAIL_KEY) ?? prompt('Confirma tu email') ?? '';
        signInWithEmailLink(auth, saved, window.location.href)
            .then(() => localStorage.removeItem(EMAIL_KEY))
            .catch(e => setError(e.message));
    }, []);

    const handleGoogle = async () => {
        await runAsyncUi({
            action: () => signInWithPopup(auth, new GoogleAuthProvider()),
            setLoading,
            setError,
        });
    };

    const handleMagicLink = async () => {
        if (!email.trim()) return;

        await runAsyncUi({
            action: () =>
                sendSignInLinkToEmail(auth, email.trim(), {
                    url: window.location.origin,
                    handleCodeInApp: true,
                }),
            setLoading,
            setError,
            onSuccess: () => {
                localStorage.setItem(EMAIL_KEY, email.trim());
                setSent(true);
            },
        });
    };

    if (sent) {
        return (
            <AuthScreen>
                <h2 style={s.title}>Revisa tu email ✉️</h2>
                <p style={s.sub}>
                    Enlace enviado a <strong>{email}</strong>.
                    <br />
                    Ábrelo desde el mismo dispositivo.
                </p>
            </AuthScreen>
        );
    }

    return (
        <AuthScreen>
            <div style={s.logo}>Process Mapper</div>
            <p style={s.tagline}>Anemonautas · Cartografía de procesos</p>

            <SecondaryButton onClick={handleGoogle} disabled={loading} style={s.googleBtn}>
                <GoogleIcon /> Entrar con Google
            </SecondaryButton>

            <div style={{ textAlign: 'center', color: '#ccc', fontSize: 11 }}>— o con email —</div>

            <AuthInput
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleMagicLink()}
                placeholder="tu@email.com"
            />
            <PrimaryButton onClick={handleMagicLink} disabled={loading || !email.trim()}>
                {loading ? 'Enviando…' : 'Enviar enlace mágico →'}
            </PrimaryButton>

            {error && <p style={{ color: '#c0392b', fontSize: 12, textAlign: 'center' }}>{error}</p>}
        </AuthScreen>
    );
};

const GoogleIcon = () => (
    <svg width="18" height="18" viewBox="0 0 18 18" style={{ marginRight: 8, flexShrink: 0 }}>
        <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.716v2.258h2.908C16.658 14.083 17.64 11.776 17.64 9.2z" />
        <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.185l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" />
        <path fill="#FBBC05" d="M3.964 10.705A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.705V4.963H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.037l3.007-2.332z" />
        <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.963L3.964 7.295C4.672 5.163 6.656 3.58 9 3.58z" />
    </svg>
);

const s: Record<string, React.CSSProperties> = {
    logo: { fontFamily: "'Caveat',cursive", fontSize: 28, fontWeight: 700, color: '#2d7a50', textAlign: 'center' },
    tagline: { fontSize: 11, color: '#aaa', textAlign: 'center', marginTop: -8 },
    title: { fontFamily: "'Caveat',cursive", fontSize: 22, color: '#2d7a50', textAlign: 'center' },
    sub: { fontSize: 13, color: '#666', textAlign: 'center', lineHeight: 1.6 },
    googleBtn: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px solid #ddd',
        background: 'white',
        fontFamily: "'DM Mono',monospace",
        fontSize: 14,
        color: '#333',
    },
};

export default Login;
